import type { QueryClient } from "@tanstack/react-query";
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { notFound } from "@tanstack/react-router";

import { loadArchiveMonthCalendar } from "#/server/game/archiveMonthCalendar";
import { loadArchiveMonthScores } from "#/server/game/archiveMonthScores";
import { getChallengeStarHiscores } from "#/server/game/getChallengeStarHiscores";
import { getMapLeaderboardScores } from "#/server/game/getMapLeaderboardScores";
import { getMedalHiscores } from "#/server/game/getMedalHiscores";
import { loadGlobalStats } from "#/server/game/globalStats";
import { getPlayerPreview } from "#/server/game/playerPreview";
import { loadProfileActivity } from "#/server/game/profileActivity";
import { loadPublicProfile } from "#/server/game/publicProfile";
import { getMyEntitlements } from "#/server/getMyEntitlements";
import { getMyPlayerProfile } from "#/server/getMyPlayerProfile";
import { getPlayerAchievements } from "#/server/getPlayerAchievements";

export function globalStatsQueryOptions() {
  return queryOptions({
    queryKey: ["globalStats"] as const,
    queryFn: () => loadGlobalStats(),
  });
}

export function playerProfileQueryOptions(userId: string) {
  return queryOptions({
    queryKey: ["myPlayerProfile", userId] as const,
    queryFn: () => getMyPlayerProfile({ data: {} }),
  });
}

export function patchMyPlayerProfileQuery({
  queryClient,
  userId,
  patch,
}: {
  queryClient: QueryClient;
  userId: string;
  patch: Partial<Awaited<ReturnType<typeof getMyPlayerProfile>>>;
}) {
  queryClient.setQueryData(playerProfileQueryOptions(userId).queryKey, (current) =>
    current ? { ...current, ...patch } : current,
  );
}

export function profileActivityQueryOptions(userId: string) {
  return queryOptions({
    queryKey: ["profileActivity", userId] as const,
    queryFn: () => loadProfileActivity({ data: {} }),
  });
}

export function publicProfileQueryOptions(name: string) {
  return queryOptions({
    queryKey: ["publicProfile", name] as const,
    queryFn: async () => {
      const data = await loadPublicProfile({ data: { name } });
      if (!data) throw notFound();
      return data;
    },
  });
}

export function playerAchievementsQueryOptions(name: string) {
  return queryOptions({
    queryKey: ["playerAchievements", name] as const,
    queryFn: async () => {
      const data = await getPlayerAchievements({ data: { name } });
      if (!data) throw notFound();
      return data;
    },
  });
}

export function playerPreviewQueryOptions(playerId: string) {
  return queryOptions({
    queryKey: ["playerPreview", playerId] as const,
    queryFn: () => getPlayerPreview({ data: { playerId } }),
    staleTime: 60_000,
  });
}

export function myEntitlementsQueryOptions(userId: string) {
  return queryOptions({
    queryKey: ["myEntitlements", userId] as const,
    queryFn: () => getMyEntitlements(),
  });
}

export function archiveMonthCalendarQueryOptions(monthKey: string) {
  return queryOptions({
    queryKey: ["archiveMonthCalendar", monthKey] as const,
    queryFn: () => loadArchiveMonthCalendar({ data: { monthKey } }),
  });
}

export function archiveMonthScoresQueryOptions(monthKey: string) {
  return queryOptions({
    queryKey: ["archiveMonthScores", monthKey] as const,
    queryFn: () => loadArchiveMonthScores({ data: { monthKey } }),
  });
}

export function mapLeaderboardInfiniteQueryOptions(mapId: string) {
  return infiniteQueryOptions({
    queryKey: ["map-leaderboard", mapId] as const,
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      getMapLeaderboardScores({ data: { mapId, cursor: pageParam } }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage: { cursor: string | null }) => lastPage.cursor ?? undefined,
  });
}

export function medalHiscoresInfiniteQueryOptions(period: "alltime" | (string & {})) {
  return infiniteQueryOptions({
    queryKey: ["medal-hiscores", period] as const,
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      getMedalHiscores({ data: { period, cursor: pageParam } }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage: { cursor: string | null }) => lastPage.cursor ?? undefined,
  });
}

export function challengeStarHiscoresInfiniteQueryOptions() {
  return infiniteQueryOptions({
    queryKey: ["challenge-star-hiscores"] as const,
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      getChallengeStarHiscores({ data: { cursor: pageParam } }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage: { cursor: string | null }) => lastPage.cursor ?? undefined,
  });
}

export async function ensureArchiveMonthQueries({
  queryClient,
  monthKey,
}: {
  queryClient: QueryClient;
  monthKey: string;
}) {
  await Promise.all([
    queryClient.ensureQueryData(archiveMonthCalendarQueryOptions(monthKey)),
    queryClient.ensureQueryData(archiveMonthScoresQueryOptions(monthKey)),
  ]);
}
